<template>
  <div
      v-if="isVisible"
      class="calculator"
      :class="{
        wide: wide,
      }"
      ref="root"
  >
    <BaseCalculatorBranchSelection
        :allowed-branches="allowedBranches"
        v-model="selectedBranch"
    />
    <div class="calculator__form">
      <div class="calculator__form--postcode">
        <InputField
            label="Deine Postleitzahl"
            :only-numeric="true"
            error-numeric="Ungültige Postleitzahl"
            :max-length="5"
            :min-length="5"
            :show-loader="isFetching"
            :is-valid="showValidCheck.postcode"
            :valid-color="[selectedBranch?.customColor.primary, selectedBranch?.customColor.secondary]"
            v-model="form.postcode"
            @keyup="checkPostcodeValid"
        />
      </div>
      <div
          v-show="showCity"
          class="calculator__form--city"
      >
        <SelectField
            label="Deine Stadt*"
            v-model="form.city"
            :readonly="selectableCities.length <= 1"
            :options="cityOptions"
            :is-valid="showValidCheck.city"
            :valid-color="[selectedBranch?.customColor.primary, selectedBranch?.customColor.secondary]"
            @update:modelValue="hasFocusedOutOnce.city = true"
        />
      </div>
      <div
          v-show="showStreet"
          class="calculator__form--street"
      >
        <SelectField
            label="Straße*"
            v-model="form.street"
            :options="streetsOptions"
            :search="true"
            :is-valid="showValidCheck.street"
            :valid-color="[selectedBranch?.customColor.primary, selectedBranch?.customColor.secondary]"
            @update:modelValue="hasFocusedOutOnce.street = true"
        />
      </div>
      <div
          v-show="showHouseNumber"
          class="calculator__form--house-number"
      >
        <div class="row g-0 align-items-center">
          <div class="col-auto col-mh"></div>
          <div class="col">
            <InputField
                label="Hausnummer*"
                type="text"
                v-model="form.houseNumber"
                :is-valid="showValidCheck.houseNumber"
                :valid-color="[selectedBranch?.customColor.primary, selectedBranch?.customColor.secondary]"
                @keyup="checkHousenumberValid"
                @focusout="hasFocusedOutOnce.houseNumber = true"
            />
          </div>
        </div>
      </div>
      <div
          v-show="showUsage"
          class="calculator__form--usage"
      >
        <BaseCalculatorUsageField
            label="Dein Verbrauch*"
            v-model="form.usage"
            :branch="selectedBranch?.mainCategory.key || 'power'"
            :is-valid="showValidCheck.usage"
            :valid-color="[selectedBranch?.customColor.primary, selectedBranch?.customColor.secondary]"
            @update:modelValue="hasFocusedOutOnce.usage = true"
            @keyup="checkUsageValid"
            @focusout="() => { checkUsageValid(); hasFocusedOutOnce.usage = true }"
        />
      </div>
      <div
          v-if="showHouseNumber"
          class="calculator__form--customer-type"
      >
        <Checkbox
            v-model="form.business"
            light
        >
          Ich bin Geschäftskund:in
        </Checkbox>
      </div>
      <div
          v-if="isInternet && wide"
          class="calculator__form--is-customer"
      >
        <Checkbox
            v-model="form.isCustomer"
            light
        >
          Ich bin Strom/Gas Kund:in
        </Checkbox>
      </div>
      <div
          v-show="showSubmitButton"
          class="calculator__form-submit"
      >
        <BaseButton
            :branch="selectedBranch"
            width="full"
            icon="fa-solid fa-arrow-right"
            :style="{
              background: `linear-gradient(90deg, ${selectedBranch?.customColor.primary}, ${selectedBranch?.customColor.secondary})`,
            }"
            @click.prevent="submit"
        >
          {{ buttonLabel }}
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import BaseButton from '@/components/BaseButton.vue';
import BaseCalculatorBranchSelection from '@/components/BaseCalculator/BaseCalculatorBranchSelection.vue';
import Checkbox from '@/components/Checkbox.vue';
import BranchInterface from '@/interfaces/BranchInterface';
import CalculatorModelInterface from '@/interfaces/CalculatorModelInterface';
import {
  reactive,
  computed,
  watch,
  ref,
  onBeforeMount, nextTick, onBeforeUnmount, WatchStopHandle,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import {
  EnetAPI,
  ProductAPI,
} from '@/api';
import InputField from '@/components/InputField.vue';
import SelectField from '@/components/SelectField.vue';
import BaseCalculatorUsageField from '@/components/BaseCalculator/BaseCalculatorUsageField.vue';
import CityInterface from '@/interfaces/CityInterface';
import StreetInterface from '@/interfaces/StreetInterface';
import ProductPost from '@/interfaces/ProductPostInterface';
import { env } from '@/helpers';

type callbackProp = (data: CalculatorModelInterface, valid: boolean) => void;

const props = withDefaults(defineProps<{
  productId?: number
  forceVisibility?: string | number | boolean
  isBusinessActive?: string | boolean
  branches?: string | string[] | BranchInterface
  postcode?: string
  buttonLabel?: string
  options?: CalculatorModelInterface
  wide?: boolean
  hideSubmit?: boolean
  callback?: callbackProp
}>(), {
  forceVisibility: false,
  isBusinessActive: false,
  branches: 'all',
  buttonLabel: 'Tarif finden',
  options: () => ({}),
});

const router = useRouter();
const route = useRoute();
const store = useStore();
const root = ref<InstanceType<typeof HTMLElement>>();

const isVisible = computed<boolean>(() => (env('CALCULATOR_ACTIVE') as boolean || [true, 'true', 1].includes((props.forceVisibility || 0))));

const allowedBranchesKeys = computed(() => (Array.isArray(props.branches)
  ? props.branches
  : `${props.branches || 'all'}`.split(',')));

const allowedBranches = computed<BranchInterface[]>(
  () => store.getters['branches/allWithUrls']
    .filter((branch: BranchInterface) => allowedBranchesKeys.value.length === 0
        || allowedBranchesKeys.value.includes(branch.slug)
        || allowedBranchesKeys.value.includes('all'))
    .sort((branch: BranchInterface, branchB: BranchInterface) => allowedBranchesKeys.value.findIndex((key) => key === branch.slug) - allowedBranchesKeys.value.findIndex((key) => key === branchB.slug)),
);

const product = ref<ProductPost>();

const valid = reactive({
  postcode: false,
  street: false,
  houseNumber: false,
  usage: false,
});

const hasFocusedOutOnce = reactive({
  postcode: false,
  city: false,
  street: false,
  houseNumber: false,
  usage: false,
});

const initialBranch = props.options.branch || (
  allowedBranches.value.length > 1 ? undefined : (allowedBranches.value[0]?.slug || 'strom')
);

const getInitialUsageByBranch = (branch?: string) => {
  switch (branch) {
    case 'gas':
      return parseInt(`${route.params.usage || 12000}`, 10);
    default:
      return parseInt(`${route.params.usage || 2500}`, 10);
  }
};

const form = ref<CalculatorModelInterface>({
  ...{
    productId: props.productId,
    branch: initialBranch,
    postcode: undefined,
    city: undefined,
    street: undefined,
    houseNumber: undefined,
    usage: getInitialUsageByBranch(initialBranch),
    business: props.isBusinessActive === 'true',
    isCustomer: false,
  },
  ...props.options,
});

const postcodeLoaded = ref<string | number>('');
const preselectedPostcodeSet = ref(false);
const selectableCities = ref<CityInterface[]>([]);
const selectableStreets = ref<StreetInterface[]>([]);

const showValidCheck = computed(() => ({
  postcode: hasFocusedOutOnce.postcode ? valid.postcode : undefined,
  city: hasFocusedOutOnce.city ? !!form.value.city : undefined,
  street: hasFocusedOutOnce.street ? !!form.value.street : undefined,
  houseNumber: hasFocusedOutOnce.houseNumber ? valid.houseNumber : undefined,
  usage: hasFocusedOutOnce.usage ? valid.usage : undefined,
}));

const selectedBranch = ref<BranchInterface>();

watch(() => selectedBranch.value?.slug, (newValue) => {
  form.value.usage = getInitialUsageByBranch(newValue);
});

const isInternet = computed<boolean>(() => selectedBranch.value?.mainCategory.key === 'internet');

const isFetching = ref(false);
const fetchCitiesAndStreets = ($postcode: string | number) => {
  if (`${$postcode}`.length === 5 && $postcode !== postcodeLoaded.value) {
    selectableCities.value = [];
    selectableStreets.value = [];
    valid.postcode = false;
    hasFocusedOutOnce.city = false;
    hasFocusedOutOnce.street = false;
    hasFocusedOutOnce.houseNumber = false;
    hasFocusedOutOnce.usage = false;

    postcodeLoaded.value = $postcode as string;

    isFetching.value = true;
    EnetAPI.getCitiesAndStreets($postcode).then((data) => {
      selectableCities.value = data.cities;
      selectableStreets.value = data.streets;
      if (!((props.options.postcode === $postcode) && !preselectedPostcodeSet.value)) {
        form.value.city = selectableCities.value[0].city;
        if (selectableCities.value.length === 1) {
          form.value.city = selectableCities.value[0].city;
          hasFocusedOutOnce.city = true;
        } else {
          form.value.city = undefined;
        }
        if (selectableStreets.value.length === 1) {
          hasFocusedOutOnce.street = true;
          form.value.street = selectableStreets.value[0].street;
        } else {
          form.value.street = undefined;
        }
        preselectedPostcodeSet.value = true;
      }
      valid.postcode = true;
    }).catch(() => {
      valid.postcode = false;
    }).finally(() => {
      isFetching.value = false;
      hasFocusedOutOnce.postcode = true;
    });
  }
};

watch(() => form.value.postcode, ($newPostcode) => {
  fetchCitiesAndStreets($newPostcode as string);

  if (!selectedBranch.value) {
    // eslint-disable-next-line prefer-destructuring
    selectedBranch.value = allowedBranches.value[0];
  }
});

if (form.value.postcode && `${form.value.postcode}`.length === 5) {
  fetchCitiesAndStreets(form.value.postcode);
}

const cityOptions = computed(() => [...selectableCities.value].map(($s) => ({
  label: $s.city,
  value: $s.city,
})));

const streetsOptions = computed(() => [...selectableStreets.value].map(($s) => ({
  label: $s.street,
  value: $s.street,
})));

const canSubmit = computed(() => selectedBranch.value
    && allowedBranches.value.filter((branchOption) => branchOption.slug === selectedBranch.value?.slug).length >= 0
    && `${form.value.postcode || ''}`.trim().length === 5
    && form.value.city !== undefined
    && form.value.street !== undefined
    && `${form.value.houseNumber || ''}`.trim().length >= 1
    && (isInternet.value || parseInt(`${form.value.usage || ''}`, 10) > 0));

const showCity = computed<boolean>(() => (
  `${form.value.postcode || ''}`.trim().length === 5
    && cityOptions.value.length > 0
));
const showStreet = computed<boolean>(() => (
  showCity.value
    && streetsOptions.value.length > 0
));
const showHouseNumber = computed<boolean>(() => showStreet.value);
const showUsage = computed<boolean>(() => showStreet.value && !isInternet.value);
const showSubmitButton = computed<boolean>(() => !props.hideSubmit && canSubmit.value);

const submit = () => {
  let path = '/preisanzeige/';

  if (!selectedBranch.value) return;
  if (!form.value.postcode) return;
  if (!form.value.city) return;
  if (!form.value.street) return;
  if (!form.value.houseNumber) return;

  if (props.callback) {
    props.callback(form.value, canSubmit.value);

    return;
  }

  const params: Record<string, string> = {
    stadt: form.value.city as string,
    strasse: form.value.street as string,
    hausnummer: form.value.houseNumber as string,
  };

  if (form.value.business) {
    params.gewerbe = '1';
  }

  if (form.value.isCustomer) {
    params.energiekunde = '1';
  }

  if (form.value.productId) {
    params.pid = `${form.value.productId}`;
  }

  const urlParams = new URLSearchParams(params);

  if (isInternet.value) {
    path += `${selectedBranch.value?.slug}/${form.value.postcode}/?${urlParams.toString()}`;
  } else {
    path += `${selectedBranch.value?.slug}/${form.value.postcode}/${form.value.usage}/?${urlParams.toString()}`;
  }

  router.push(path);
};

const checkPostcodeValid = () => {
  valid.postcode = `${form.value.postcode || ''}`.trim().length === 5 && selectableCities.value.length > 0;
};

const checkUsageValid = () => {
  valid.usage = parseInt(`${form.value.usage || ''}`, 10) > 0;
};

watch(() => form.value.usage, checkUsageValid);

const checkHousenumberValid = () => {
  valid.houseNumber = `${form.value.houseNumber || ''}`.trim().length >= 1;
};

let callbackWatcher: WatchStopHandle;

onBeforeMount(() => {
  if (props.productId !== undefined) {
    ProductAPI.get(props.productId).then((apiProduct) => {
      product.value = apiProduct;

      if (apiProduct) {
        form.value.branch = apiProduct.branch || 'power';

        const apiProductBranch = allowedBranches.value.find((branch) => branch.slug === form.value.branch);
        if (apiProductBranch) {
          selectedBranch.value = apiProductBranch;
        }
      }
    });
  }

  if (props.hideSubmit && props.callback) {
    callbackWatcher = watch(() => form, () => {
      nextTick(() => {
        if (props.callback) {
          props.callback(form.value, canSubmit.value);
        }
      });
    }, {
      deep: true,
    });
  }
});

onBeforeUnmount(() => {
  if (callbackWatcher) {
    callbackWatcher();
  }
});

watch(() => allowedBranches.value, () => {
  if (props.options.branch) {
    const preselectedBranch = allowedBranches.value.find((branch) => branch.slug === props.options.branch);

    if (preselectedBranch) {
      selectedBranch.value = preselectedBranch;
    }
  }
}, {
  deep: true,
  immediate: true,
});
</script>

<style lang="scss">
@import "src/scss/component-helpers";

.calculator {
  width: 100%;
  max-width: 100%;
  display: grid;
  grid-template-columns: 100%;
  grid-gap: 16px;

  @media(min-width: map_get($grid-breakpoints, 'lg')) {
    gap: 40px;
  }

  .calculator__form {
    display: grid;
    flex-wrap: wrap;
    gap: 16px;
    grid-template-columns: 1fr 1fr;

    @media(min-width: map_get($grid-breakpoints, 'lg')) {
      gap: 30px;
    }
  }

  &.wide {
    .calculator__form {
      display: grid;
      grid-template-columns: 1fr 1fr;

      @media(min-width: 1680px) {
        grid-template-columns: repeat(5, 1fr);
      }

      &--customer-type, &--is-customer {
        grid-row: 3 / 4;

        @media(min-width: 1680px) {
          grid-row: 2 / 3;
        }
      }
    }
  }
}
</style>
